import { useEffect, useRef, useState } from 'react'

import { InfoIcon } from 'lucide-react'
import { Tooltip } from 'react-tooltip'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import ClockIcon from '@src/components/icons/Clock'
import ExchangeIcon from '@src/components/icons/Exchange'
import PointIcon from '@src/components/icons/Point'
import SuccessIcon from '@src/components/icons/Success'
import UpArrowCorner from '@src/components/icons/UpArrowCorner'
import { ChainId } from '@src/configs/chainInfo'
import { SOLANA_TOKEN_ACCOUNT_TOOLTIP } from '@src/constants/exchange'
import { LiquiditySource } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { SourceTxComponent } from '@src/pages/Exchange/components/SwapInProgressFlow/SourceTxComponent'
import SwapModalFooter from '@src/pages/Exchange/components/SwapInProgressFlow/SwapModalFooter'
import { analyticsService } from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { computeUSDAmount } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { formatExchangeAmountWithCommas, isPotentiallyCCTP, isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { formatNumber } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useConfigStore } from '@src/zustand_stores'

interface ISwapCompletedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    result?: {
        sourceTxHash?: string
        destinationTxHash?: string
    }
    miles?: number
    fullTradeTimeTimer: any
    liquiditySource?: LiquiditySource
}

export default function SwapCompleted({
    sellData,
    buyData,
    result = undefined,
    miles,
    fullTradeTimeTimer,
    liquiditySource,
}: ISwapCompletedProps) {
    // Track when the SwapCompleted component is rendered with confirmed completion
    const [eventTracked, setEventTracked] = useState(false)
    const eventAttempts = useRef(0)

    // Get wallet connections at the component level
    const { evmWalletAddress, solanaWalletAddress, tronWalletAddress } = useWalletConnections()

    // Function to track the completion event
    const trackCompletionEvent = () => {
        // Only track once
        if (eventTracked) return

        // Increment attempt counter for debugging
        eventAttempts.current += 1

        // Only track the frontend completion event if we have confirmation from the backend
        // (via a destination transaction hash which indicates backend confirmation)
        if (result?.destinationTxHash) {
            // Determine if this is a CCTP swap (USDC to USDC across chains)
            const isCCTP = isPotentiallyCCTP(
                sellData.token?.symbol,
                buyData.token?.symbol,
                sellData.chainId,
                buyData.chainId,
                liquiditySource
            )

            // Calculate USD equivalents
            const sourceAmountUSD = computeUSDAmount(sellData.token, sellData.value)
            const destinationAmountUSD = computeUSDAmount(buyData.token, buyData.value)

            analyticsService.trackEvent('trade_completed_fe', {
                source_token: sellData.token?.symbol,
                source_chain: sellData.chainId,
                source_amount: sellData.value.toString(),
                source_amount_usd: sourceAmountUSD,
                source_token_price: sellData.token?.price_current,
                destination_token: buyData.token?.symbol,
                destination_chain: buyData.chainId,
                destination_amount: buyData.value.toString(),
                destination_amount_usd: destinationAmountUSD,
                destination_token_price: buyData.token?.price_current,
                miles_earned: miles,
                swap_time: formatTime(),
                has_destination_tx: !!result?.destinationTxHash,
                place_tx: result?.sourceTxHash,
                fill_tx: result?.destinationTxHash,
                is_cctp: isCCTP,
                swap_type: isCCTP ? 'cctp' : 'market_maker',
                backend_confirmed: true,
                attempts: eventAttempts.current,
                was_tabbed_in: document.visibilityState === 'visible',
            })

            // Update wallet value after transaction completes
            // Get the connected wallet address
            const walletAddress =
                evmWalletAddress ||
                (solanaWalletAddress ? solanaWalletAddress.toString() : undefined) ||
                tronWalletAddress

            if (walletAddress) {
                // Determine chain type based on source chain
                let chainType = 'EVM'
                if (sellData.chainId === ChainId.Solana) {
                    chainType = 'SVM'
                } else if (sellData.chainId === ChainId.Tron) {
                    chainType = 'TRX'
                }

                // Update wallet value in PostHog
                analyticsService.updateWalletValueAfterTransaction(walletAddress, chainType)
            }

            setEventTracked(true)
        } else {
            // If we don't have backend confirmation yet, track as a partial completion
            // Determine if this is a CCTP swap (USDC to USDC across chains)
            const isCCTP = isPotentiallyCCTP(
                sellData.token?.symbol,
                buyData.token?.symbol,
                sellData.chainId,
                buyData.chainId,
                liquiditySource
            )

            // Calculate USD equivalents
            const sourceAmountUSD = computeUSDAmount(sellData.token, sellData.value)
            const destinationAmountUSD = computeUSDAmount(buyData.token, buyData.value)

            analyticsService.trackEvent('trade_completion_view', {
                source_token: sellData.token?.symbol,
                source_chain: sellData.chainId,
                source_amount: sellData.value.toString(),
                source_amount_usd: sourceAmountUSD,
                source_token_price: sellData.token?.price_current,
                destination_token: buyData.token?.symbol,
                destination_chain: buyData.chainId,
                destination_amount: buyData.value.toString(),
                destination_amount_usd: destinationAmountUSD,
                destination_token_price: buyData.token?.price_current,
                miles_earned: miles,
                swap_time: formatTime(),
                place_tx: result?.sourceTxHash,
                is_cctp: isCCTP,
                swap_type: isCCTP ? 'cctp' : 'market_maker',
                backend_confirmed: false,
                attempts: eventAttempts.current,
                was_tabbed_in: document.visibilityState === 'visible',
            })
        }
    }

    // Track event when component mounts
    useEffect(() => {
        trackCompletionEvent()

        // Also track when visibility changes (user tabs back in)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                trackCompletionEvent()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const formattedDestinationTxnHash = truncateAddress(
        result?.destinationTxHash?.substring(result.destinationTxHash.lastIndexOf('/') + 1) || ''
    )
    const destinationTxnHashURL = result?.destinationTxHash?.substring(result.destinationTxHash.lastIndexOf('/') + 1)

    const isDestinationSolana = buyData.chainId === ChainId.Solana

    // Format the time string
    const formatTime = () => {
        if (!fullTradeTimeTimer) return '0s'

        const { seconds, minutes, hours } = fullTradeTimeTimer
        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`
        } else {
            return `${seconds}s`
        }
    }

    return (
        <div className='px-4'>
            <div className='space-y-4'>
                <div className='flex w-full items-center justify-center gap-[6px]'>
                    <SuccessIcon />
                    <p className='flex-10 text-base font-medium text-mach-mach-green'>Swap Complete</p>
                </div>
                <div className='relative flex'>
                    <img src={'/assets/image/ticket.png'} alt='' />
                    <div className='absolute left-0 top-0 h-full w-full p-4'>
                        <div className='flex flex-col items-center justify-center gap-4'>
                            <img src={'/assets/image/plane.png'} alt='' />
                            <div
                                className='relative h-[131px] w-full rounded-lg border-[]'
                                style={{
                                    background:
                                        'conic-gradient(from 180deg at 50% 50%, #3E73C41A 0deg, #349FFD1A 144deg, rgba(52, 159, 253, 0.1) 227.82deg, #3E73C41A 360deg)',
                                    boxShadow:
                                        '0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 1px #000000, inset 0px 1px 0px rgba(255, 255, 255, 0.25)',
                                }}
                            >
                                <div className='absolute inset-0 rounded-xl border border-[#349FFD] blur-[2px]'></div>
                                <div className='mt-[43px] flex gap-[10px] px-[15px]'>
                                    <div className='flex flex-1 flex-col gap-y-2'>
                                        <div className='flex items-center gap-x-1'>
                                            <ChainToken
                                                tokenLogo={sellData.token?.logoURI}
                                                networkIcon={availableNetworks[sellData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='max-w-20 truncate text-2xl font-medium'>
                                                {sellData.token?.symbol}
                                            </span>
                                        </div>
                                        <span>{formatExchangeAmountWithCommas(sellData)}</span>
                                    </div>
                                    <div className='mx-auto flex w-1/3 flex-col items-center justify-center gap-3'>
                                        <div className='flex items-center'>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                            <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#0F1011]'>
                                                <div className='flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#16171A]'>
                                                    <ExchangeIcon className='h-3 w-3' />
                                                </div>
                                            </div>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                        </div>
                                        <div className='flex items-center justify-center gap-1'>
                                            <ClockIcon />
                                            <span className='text-[10px] font-medium text-mach-sub-text'>
                                                Arrival: {formatTime()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className='flex flex-1 flex-col gap-y-2 self-end'>
                                        <div className='flex items-center justify-end gap-x-1'>
                                            <ChainToken
                                                tokenLogo={buyData.token?.logoURI}
                                                networkIcon={availableNetworks[buyData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='max-w-20 truncate text-2xl font-medium'>
                                                {buyData.token?.symbol}
                                            </span>
                                        </div>
                                        <div className='flex items-center justify-end gap-1'>
                                            {isDestinationSolana && (
                                                <>
                                                    <span
                                                        className='cursor-help text-gray-500'
                                                        id='solana-token-account-tooltip-completed'
                                                    >
                                                        <InfoIcon size={16} />
                                                    </span>
                                                    <Tooltip
                                                        anchorId='solana-token-account-tooltip-completed'
                                                        place='top'
                                                        className='z-[9999] max-w-xs rounded-md bg-gray-800 p-2 text-xs text-white shadow-lg'
                                                        positionStrategy='fixed'
                                                    >
                                                        {SOLANA_TOKEN_ACCOUNT_TOOLTIP}
                                                    </Tooltip>
                                                </>
                                            )}
                                            <span className='text-right'>
                                                {formatExchangeAmountWithCommas(buyData)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mt-8 flex flex-col gap-6'>
                            <span className='mb-2 text-lg font-medium text-mach-accent-forgeround'>Details</span>
                            <SourceTxComponent sourceTxHash={result?.sourceTxHash}></SourceTxComponent>
                            {!isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData) ? (
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-mach-sub-text'>Destination Id</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <CopyToClipboard
                                            anchorId='dest-tx-hash'
                                            textToCopy={destinationTxnHashURL || ''}
                                        />
                                        <span className='text-sm'>{formattedDestinationTxnHash}</span>
                                        <a
                                            className='flex cursor-pointer text-mach-sub-text'
                                            href={result?.destinationTxHash}
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            <UpArrowCorner className='h-3 w-3' />
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {!isFeatureEnabled('USE_TESTNETS_ONLY') && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-mach-sub-text'>Miles earned</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <PointIcon height='32' width='32' />
                                        <span className='text-[28px]'>{formatNumber(miles?.toString())}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className='mt-3'>
                <SwapModalFooter isSwapCompleted />
            </div>
        </div>
    )
}
